import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_API_KEY } from '../utils/constants';

const LocationPickerModal = ({ open, onClose, onSelect, initialLocation }) => {
    const mapRef = useRef(null);
    const [selectedLocation, setSelectedLocation] = useState(initialLocation || { lat: 30.7333, lng: 76.7794 }); // Default to Chandigarh/Some center
    const [address, setAddress] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const googleMaps = useRef(null);
    const mapInstance = useRef(null);
    const markerInstance = useRef(null);

    useEffect(() => {
        if (open) {
            const loadMap = async () => {
                const loader = new Loader({
                    apiKey: GOOGLE_MAPS_API_KEY,
                    version: 'weekly',
                    libraries: ['places', 'marker']
                });

                const google = await loader.load();
                googleMaps.current = google;

                let defaultLocation = initialLocation?.lat && initialLocation?.lng
                    ? { lat: parseFloat(initialLocation.lat), lng: parseFloat(initialLocation.lng) }
                    : { lat: 26.9124, lng: 75.7873 }; // Default to Jaipur center if everything fails

                const map = new google.maps.Map(mapRef.current, {
                    center: defaultLocation,
                    zoom: 15,
                    mapId: '4504f8b37365c3d0', // Required for AdvancedMarkerElement
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false
                });
                mapInstance.current = map;

                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: defaultLocation,
                    map: map,
                    gmpDraggable: true // Advanced markers use gmpDraggable
                });
                markerInstance.current = marker;

                // Try to get actual browser location AFTER map/marker are ready
                if (!initialLocation?.lat && navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const currentPos = {
                                lat: position.coords.latitude,
                                lng: position.coords.longitude
                            };
                            map.setCenter(currentPos);
                            marker.position = currentPos;
                            reverseGeocode(currentPos);
                        },
                        (error) => {
                            console.warn("Geolocation failed or denied:", error);
                        },
                        { enableHighAccuracy: true }
                    );
                }

                // Fetch initial address if coordinates exist from parent
                if (initialLocation?.lat && initialLocation?.lng) {
                    reverseGeocode(defaultLocation);
                }

                google.maps.event.addListener(map, 'click', (event) => {
                    const lat = event.latLng.lat();
                    const lng = event.latLng.lng();
                    const newPos = { lat, lng };
                    marker.position = newPos;
                    reverseGeocode(newPos);
                });

                marker.addListener('dragend', () => {
                    const newPos = {
                        lat: marker.position.lat,
                        lng: marker.position.lng
                    };
                    reverseGeocode(newPos);
                });
            };

            loadMap();
        }
    }, [open]);

    const reverseGeocode = (latLng) => {
        setIsLoading(true);
        const geocoder = new googleMaps.current.maps.Geocoder();
        geocoder.geocode({ location: latLng }, (results, status) => {
            // console.log("Full Geocoding API Response:", results);
            if (status === 'OK') {
                if (results[0]) {
                    const result = results[0];
                    setAddress(result.formatted_address);

                    const addressData = {
                        formatted_address: result.formatted_address,
                        lat: latLng.lat,
                        lng: latLng.lng,
                        address: '',
                        city: '',
                        postalCode: '',
                        country: '',
                        state: ''
                    };

                    let building = '';
                    let route = '';
                    let sublocality1 = '';
                    let sublocality2 = '';
                    let sublocality3 = '';
                    let neighborhood = '';

                    result.address_components.forEach(component => {
                        const types = component.types;
                        if (types.includes('premise') || types.includes('subpremise') || types.includes('street_number')) {
                            building = (building ? building + ', ' : '') + component.long_name;
                        } else if (types.includes('route')) {
                            route = component.long_name;
                        } else if (types.includes('neighborhood')) {
                            neighborhood = component.long_name;
                        } else if (types.includes('sublocality_level_3')) {
                            sublocality3 = component.long_name;
                        } else if (types.includes('sublocality_level_2')) {
                            sublocality2 = component.long_name;
                        } else if (types.includes('sublocality_level_1')) {
                            sublocality1 = component.long_name;
                        } else if (types.includes('locality')) {
                            addressData.city = component.long_name;
                        } else if (types.includes('administrative_area_level_2') && !addressData.city) {
                            addressData.city = component.long_name;
                        }

                        if (types.includes('postal_code')) {
                            addressData.postalCode = component.long_name;
                        } else if (types.includes('country')) {
                            addressData.country = component.long_name;
                        } else if (types.includes('administrative_area_level_1')) {
                            addressData.state = component.long_name;
                        }
                    });

                    // Construct an exhaustive full street address
                    const fullStreetAddress = [building, route, neighborhood, sublocality3, sublocality2, sublocality1]
                        .filter(Boolean)
                        .join(', ');
                    addressData.address = fullStreetAddress || result.formatted_address;

                    setSelectedLocation(addressData);
                }
            }
            setIsLoading(false);
        });
    };

    const handleConfirm = () => {
        onSelect(selectedLocation);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Select Location from Map
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Drag the marker or click on the map to select a location.
                    </Typography>
                    {isLoading ? (
                        <Typography variant="subtitle2" sx={{ mt: 1, color: 'primary.main' }}>
                            Fetching address details...
                        </Typography>
                    ) : address && (
                        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            Selected Address: {address}
                        </Typography>
                    )}
                </Box>
                <Box
                    ref={mapRef}
                    sx={{
                        width: '100%',
                        height: '400px',
                        borderRadius: 1,
                        border: '1px solid #ddd'
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={isLoading || !selectedLocation}
                >
                    {isLoading ? 'Wait...' : 'Confirm Selection'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LocationPickerModal;
